<!-- src/lib/components/DevView.svelte -->
<!--
  VS Code / Cursor-style development workspace. Three resizable columns:

    1. LEFT (260px default): FileExplorer tabbed with Git + Preview
    2. CENTER (flex): file content preview / editor
    3. RIGHT (420px default): terminal running the user's shell
       (intended for Claude Code — right-column placement gives the
       conversation vertical height)

  Unlike the global file-explorer pane (⌘B) and global terminal
  drawer (Ctrl+`), which float as ad-hoc panels accessible from any
  view, this is a dedicated full-screen section for when the user
  wants to live inside the dev loop. Same underlying components
  though — no logic duplication.

  Splitters between columns are drag-to-resize, with min/max clamps.

  CC mode toggle (top-right corner of the right pane header):
    - "Terminal": shows the embedded TerminalPanel (default)
    - "code-server": replaces the three-column layout with an iframe
      pointing at a configurable URL (default http://localhost:8080).
      The URL is read from localStorage key 'agentboard.codeServerUrl'.
-->
<script lang="ts">
  import FileExplorer from './FileExplorer.svelte';
  import FilePreview from './FilePreview.svelte';
  import GitPanel from './GitPanel.svelte';
  import LazyTerminalPanel from './LazyTerminalPanel.svelte';

  interface Props {
    fileToOpen?: string | null;
    onFileOpened?: () => void;
  }
  let { fileToOpen = null, onFileOpened }: Props = $props();

  // Open file from external source (e.g. CMD+P palette)
  $effect(() => {
    if (fileToOpen) {
      openFile(fileToOpen);
      onFileOpened?.();
    }
  });

  let leftWidth  = $state(280);
  let rightWidth = $state(440);

  type LeftTab = 'files' | 'git';
  let leftTab = $state<LeftTab>('files');

  let rootPath = $state<string | null>(null);
  let selectedFilePath = $state<string | null>(null);

  // ── File tabs ────────────────────────────────────────────────────────
  let openFiles = $state<{path: string; label: string}[]>([]);
  let activeFileIdx = $state(0);

  const activeFilePath = $derived(openFiles[activeFileIdx]?.path ?? selectedFilePath);

  function openFile(path: string) {
    selectedFilePath = path;
    const label = path.split('/').pop() ?? path;
    const existing = openFiles.findIndex(f => f.path === path);
    if (existing !== -1) {
      activeFileIdx = existing;
      return;
    }
    if (openFiles.length >= 8) {
      openFiles = [...openFiles.slice(1), { path, label }];
      activeFileIdx = openFiles.length - 1;
    } else {
      openFiles = [...openFiles, { path, label }];
      activeFileIdx = openFiles.length - 1;
    }
  }

  function closeFile(idx: number) {
    openFiles = openFiles.filter((_, i) => i !== idx);
    if (activeFileIdx >= openFiles.length) activeFileIdx = Math.max(0, openFiles.length - 1);
  }

  // ── Split terminal ───────────────────────────────────────────────────
  let splitTerminal = $state(false);

  // ── Splitter drag state ──────────────────────────────────────────────
  type Drag =
    | { kind: 'idle' }
    | { kind: 'left';  startX: number; startWidth: number }
    | { kind: 'right'; startX: number; startWidth: number };
  let drag = $state<Drag>({ kind: 'idle' });

  function onLeftSplitterPointerDown(e: PointerEvent) {
    drag = { kind: 'left', startX: e.clientX, startWidth: leftWidth };
    (e.target as Element).setPointerCapture?.(e.pointerId);
  }
  function onRightSplitterPointerDown(e: PointerEvent) {
    drag = { kind: 'right', startX: e.clientX, startWidth: rightWidth };
    (e.target as Element).setPointerCapture?.(e.pointerId);
  }
  function onPointerMove(e: PointerEvent) {
    if (drag.kind === 'left') {
      const dx = e.clientX - drag.startX;
      leftWidth = Math.max(200, Math.min(560, drag.startWidth + dx));
    } else if (drag.kind === 'right') {
      const dx = e.clientX - drag.startX;
      // Dragging the right splitter leftward (negative dx) widens the terminal
      rightWidth = Math.max(280, Math.min(800, drag.startWidth - dx));
    }
  }
  function onPointerUp(e: PointerEvent) {
    if (drag.kind !== 'idle') {
      drag = { kind: 'idle' };
      (e.target as Element).releasePointerCapture?.(e.pointerId);
    }
  }
</script>

<svelte:window onpointermove={onPointerMove} onpointerup={onPointerUp} />

<div class="dev-view">
  <aside class="dev-left" style="width: {leftWidth}px;">
    <nav class="dev-tabs">
      <button class="dev-tab" class:active={leftTab === 'files'} onclick={() => { leftTab = 'files'; }}>Files</button>
      <button class="dev-tab" class:active={leftTab === 'git'} onclick={() => { leftTab = 'git'; }}>Git</button>
    </nav>
    <div class="dev-left-body">
      {#if leftTab === 'files'}
        <FileExplorer
          {rootPath}
          onRootChange={(p) => { rootPath = p; }}
          onSelectFile={(p) => { openFile(p); }}
        />
      {:else if leftTab === 'git'}
        <GitPanel {rootPath} />
      {/if}
    </div>
  </aside>

  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div class="splitter" onpointerdown={onLeftSplitterPointerDown} role="separator" aria-orientation="vertical"></div>

  <section class="dev-center">
    {#if openFiles.length > 0}
      <div class="file-tabs" role="tablist">
        {#each openFiles as f, i (f.path)}
          <button
            class="file-tab"
            class:active={activeFileIdx === i}
            role="tab"
            aria-selected={activeFileIdx === i}
            onclick={() => { activeFileIdx = i; }}
          >
            <span class="file-tab-label">{f.label}</span>
            <!-- svelte-ignore a11y_click_events_have_key_events -->
            <span
              class="file-tab-close"
              role="button"
              tabindex="-1"
              onclick={(e) => { e.stopPropagation(); closeFile(i); }}
            >×</span>
          </button>
        {/each}
      </div>
    {/if}
    <FilePreview path={activeFilePath} />
  </section>

  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div class="splitter" onpointerdown={onRightSplitterPointerDown} role="separator" aria-orientation="vertical"></div>

  <aside class="dev-right" style="width: {rightWidth}px;">
    <div class="dev-right-header">
      <span class="dev-right-title">Terminal</span>
      <button
        class="split-btn"
        class:active={splitTerminal}
        onclick={() => { splitTerminal = !splitTerminal; }}
        title="Split terminal"
      >⊟</button>
    </div>
    {#if splitTerminal}
      <div class="terminal-split">
        <div class="terminal-half"><LazyTerminalPanel /></div>
        <div class="terminal-divider"></div>
        <div class="terminal-half"><LazyTerminalPanel /></div>
      </div>
    {:else}
      <LazyTerminalPanel />
    {/if}
  </aside>
</div>

<style>
  .dev-view {
    display: flex;
    flex: 1;
    min-height: 0;
    width: 100%;
    background: oklch(0.065 0.015 275);
    overflow: hidden;
  }

  /* ── LEFT column (file tree + git — panel tone) ──────────────── */
  .dev-left {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    min-width: 200px;
    /* Elevated panel tone so it clearly separates from the dark void
       on either side. */
    background: oklch(0.11 0.02 275);
    border-right: 1px solid oklch(0.35 0.06 275 / 0.35);
  }
  .dev-tabs {
    flex-shrink: 0;
    display: flex;
    border-bottom: 1px solid oklch(0.30 0.05 275 / 0.25);
    background: oklch(0.075 0.016 275);
  }
  .dev-tab {
    flex: 1;
    background: transparent;
    border: none;
    color: oklch(0.60 0.05 310 / 0.7);
    padding: 0.65rem 0.6rem;
    cursor: pointer;
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.09em;
    border-bottom: 2px solid transparent;
    transition: background-color 100ms ease, color 100ms ease, border-color 100ms ease;
  }
  .dev-tab:hover {
    background: oklch(0.72 0.28 310 / 0.06);
    color: oklch(0.85 0.10 310);
  }
  .dev-tab.active {
    color: oklch(0.94 0.12 310);
    border-bottom-color: oklch(0.78 0.26 310 / 0.7);
  }
  .dev-left-body {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  /* ── Splitter ─────────────────────────────────────────────────── */
  .splitter {
    width: 1px;
    flex-shrink: 0;
    cursor: col-resize;
    background: oklch(0.35 0.06 275 / 0.35);
    transition: background-color 120ms ease;
    position: relative;
  }
  /* Hit target wider than the visible 1px line */
  .splitter::after {
    content: '';
    position: absolute;
    top: 0; bottom: 0;
    left: -3px;
    right: -3px;
  }
  .splitter:hover,
  .splitter:active {
    background: oklch(0.78 0.26 310 / 0.7);
  }

  /* ── CENTER column (file preview — reading surface) ──────────── */
  .dev-center {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    /* Slightly lighter than the left panel so the "page being read"
       is the most prominent zone. */
    background: oklch(0.085 0.016 275);
    border-right: 1px solid oklch(0.35 0.06 275 / 0.35);
    border-left:  1px solid oklch(0.35 0.06 275 / 0.35);
  }

  /* ── RIGHT column (terminal — deeper void) ──────────────────── */
  .dev-right {
    flex-shrink: 0;
    min-width: 280px;
    display: flex;
    flex-direction: column;
    /* Darker than either neighbour so the terminal recedes and its
       ANSI output pops. Same trick that makes the real macOS Terminal
       feel like a tunnel through the desktop. */
    background: oklch(0.05 0.012 275);
  }

  .dev-right-header {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.35rem 0.6rem;
    border-bottom: 1px solid oklch(0.35 0.06 275 / 0.25);
    background: oklch(0.075 0.016 275);
    gap: 0.5rem;
  }

  .dev-right-title {
    font-size: 0.70rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.09em;
    color: oklch(0.60 0.05 310 / 0.7);
  }

  /* ── File tabs (Improvement #8) ──────────────────────────────── */
  .file-tabs {
    display: flex;
    overflow-x: auto;
    background: oklch(0.075 0.016 275);
    border-bottom: 1px solid oklch(0.30 0.05 275 / 0.25);
    flex-shrink: 0;
    gap: 0;
    scrollbar-width: none;
  }
  .file-tabs::-webkit-scrollbar { display: none; }
  .file-tab {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.4rem 0.75rem;
    background: transparent;
    border: none;
    border-right: 1px solid oklch(0.30 0.05 275 / 0.20);
    color: oklch(0.60 0.05 310 / 0.7);
    font-size: 0.72rem;
    cursor: pointer;
    white-space: nowrap;
    flex-shrink: 0;
    transition: background 100ms, color 100ms;
  }
  .file-tab:hover { background: oklch(0.12 0.02 275); color: oklch(0.85 0.08 310); }
  .file-tab.active {
    background: oklch(0.085 0.016 275);
    color: oklch(0.95 0.08 310);
    border-bottom: 2px solid oklch(0.72 0.28 310 / 0.8);
  }
  .file-tab-close {
    opacity: 0;
    transition: opacity 100ms;
    font-size: 0.85rem;
    line-height: 1;
    padding: 0 0.1rem;
    border-radius: 2px;
  }
  .file-tab:hover .file-tab-close,
  .file-tab.active .file-tab-close { opacity: 0.6; }
  .file-tab-close:hover { opacity: 1 !important; }

  /* ── Split terminal (Improvement #11) ────────────────────────── */
  .split-btn {
    background: transparent;
    border: 1px solid oklch(0.35 0.06 275 / 0.4);
    color: oklch(0.60 0.05 310 / 0.7);
    border-radius: 4px;
    width: 22px; height: 22px;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; font-size: 0.8rem;
    transition: background 100ms, color 100ms;
  }
  .split-btn:hover, .split-btn.active {
    background: oklch(0.72 0.28 310 / 0.15);
    color: oklch(0.94 0.12 310);
    border-color: oklch(0.72 0.28 310 / 0.4);
  }
  .terminal-split {
    flex: 1; min-height: 0;
    display: flex; flex-direction: column;
  }
  .terminal-half { flex: 1; min-height: 0; display: flex; flex-direction: column; overflow: hidden; }
  .terminal-divider {
    height: 4px; flex-shrink: 0;
    background: oklch(0.30 0.05 275 / 0.40);
    cursor: row-resize;
  }
</style>
